import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { NOTIFICATION_CONTENT_TYPE, NOTIFICATION_TYPE } from "@/api";
import { Chat, Heart01 } from "@/icons";
import { useCommunityGroups, useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import { useSendCommunityNotification } from "../../hooks/useSendCommunityNotification";
import { Reactions } from "../../types";
import { toggleReaction } from "../../util";

const ICON_SIZE = {
  width: 20,
  height: 20,
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  active?: boolean;
}

const Button = ({
  icon,
  value,
  active,
  title,
  disabled,
  ...props
}: ButtonProps) => {
  const { translate } = useTranslate();

  return (
    <button
      type="button"
      className={cn(
        "flex h-[32px] items-center justify-center gap-1 rounded-[16px] px-3 py-1",
        "transition-colors duration-100",
        "text-item-secondary",
        !disabled &&
          "cursor-pointer hover:bg-surface-secondary active:bg-surface-tertiary active:text-item-primary",
        !disabled &&
          active &&
          "hover:bg-heart-surface active:bg-heart-surface active:text-heart-item",
        active && "text-heart-item",
      )}
      title={translate(title ?? "")}
      disabled={disabled}
      {...props}
    >
      {icon}
      <div className="caption-medium">{value}</div>
    </button>
  );
};

export const LikeButton = ({
  haveLiked,
  likesCount,
  type,
  item,
  overrideClick,
}: {
  haveLiked: boolean;
  likesCount: number;
  type: Amity.ReactableType;
  item: {
    id: string;
    type: "post" | "comment";
    textContent: string;
    userId: string;
  };
  overrideClick?: () => void;
}) => {
  const [isActive, setIsActive] = useState(haveLiked);
  const [likes, setLikes] = useState(likesCount);
  const [isLoading, setIsLoading] = useState(false);
  const { translate } = useTranslate();
  const sendNotification = useSendCommunityNotification();
  const { activeGroup } = useCommunityGroups();

  const communityId = activeGroup?.CommunityID;

  const isProcessingUserActionRef = useRef(false);

  useEffect(() => {
    if (isProcessingUserActionRef.current) {
      return;
    }

    setIsActive(haveLiked);
    setLikes(likesCount);
  }, [haveLiked, likesCount]);

  const onClick = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    // Store current state for potential revert
    const previousActive = isActive;
    const previousLikes = likes;

    // Optimistic update
    const newActive = !isActive;
    const newLikes = newActive ? likes + 1 : likes - 1;
    const actionType = newActive ? "add" : "remove";

    setIsActive(newActive);
    setLikes(newLikes);

    try {
      await toggleReaction(actionType, type, item.id, Reactions.like);
      if (actionType === "add" && communityId) {
        sendNotification.mutate({
          notification_type: NOTIFICATION_TYPE.reactions,
          content_type:
            item.type === "comment"
              ? NOTIFICATION_CONTENT_TYPE.comment
              : NOTIFICATION_CONTENT_TYPE.post,
          content_id: item.id,
          community_id: communityId,
        });
      }
    } catch (error) {
      // Revert to previous state on error
      setIsActive(previousActive);
      setLikes(previousLikes);
      setIsLoading(true);

      toast(
        actionType === "add"
          ? `${translate("Failed to like")} ${translate(type)}`
          : `${translate("Failed to unlike")} ${translate(type)}`,
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [
    type,
    item,
    isActive,
    likes,
    isLoading,
    communityId,
    sendNotification,
    translate,
  ]);

  return (
    <Button
      icon={
        <Heart01 fill={isActive ? "currentColor" : "none"} {...ICON_SIZE} />
      }
      value={likes}
      active={isActive}
      title={isActive ? translate("Unlike") : translate("Like")}
      onClick={overrideClick ?? onClick}
      disabled={isLoading}
    />
  );
};

export const CommentButton = ({
  value,
  onClick,
}: {
  value: number;
  onClick: () => void;
}) => {
  const { translate } = useTranslate();

  return (
    <Button
      icon={<Chat {...ICON_SIZE} />}
      value={value}
      title={translate("View Comments")}
      onClick={onClick}
    />
  );
};
